import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

/**
 * Custom hook implementing the roving tabindex pattern for keyboard navigation.
 * Only one item in the list is tabbable at a time (tabIndex=0), all others have tabIndex=-1.
 * Arrow keys move focus between items.
 *
 * @returns {import('./RovingTabIndexContext').RovingTabIndexContext} - Roving tabindex context
 */
export const useRovingTabIndexSetup = () => {
  const [activeId, setActiveId] = useState(null);
  const itemsRef = useRef(new Map()); // Map<id, { ref }>

  // Use a ref to track activeId for callbacks to avoid stale closures
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  /**
   * Get items sorted by DOM order
   */
  const getSortedItems = useCallback(() => {
    const items = Array.from(itemsRef.current.entries());

    // Sort by DOM position
    items.sort(([, a], [, b]) => {
      const elA = a.ref?.current;
      const elB = b.ref?.current;
      if (!elA || !elB) return 0;

      const position = elA.compareDocumentPosition(elB);
      // eslint-disable-next-line no-bitwise
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      // eslint-disable-next-line no-bitwise
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    return items;
  }, []);

  /**
   * Focus the item with the given id
   */
  const focusItem = useCallback((id) => {
    const item = itemsRef.current.get(id);
    if (item?.ref?.current) {
      item.ref.current.focus();
    }
  }, []);

  /**
   * Register a new item
   */
  const registerItem = useCallback((id, ref) => {
    itemsRef.current.set(id, { ref });

    // If no active item, set the first registered one
    if (activeIdRef.current === null) {
      setActiveId(id);
    }
  }, []);

  /**
   * Unregister an item
   */
  const unregisterItem = useCallback(
    (id) => {
      itemsRef.current.delete(id);

      // If the active item was removed, select the first available
      if (activeIdRef.current === id) {
        const items = getSortedItems();
        if (items.length > 0) {
          setActiveId(items[0][0]);
        } else {
          setActiveId(null);
        }
      }
    },
    [getSortedItems],
  );

  /**
   * Reset all items and active state
   */
  const reset = useCallback(() => {
    itemsRef.current.clear();
    setActiveId(null);
  }, []);

  /**
   * Move focus to the next item
   */
  const focusNext = useCallback(() => {
    const items = getSortedItems();
    if (items.length === 0) return;

    const currentId = activeIdRef.current;
    const currentIndex = items.findIndex(([id]) => id === currentId);
    const nextIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
    const nextId = items[nextIndex][0];

    setActiveId(nextId);
    focusItem(nextId);
  }, [getSortedItems, focusItem]);

  /**
   * Move focus to the previous item
   */
  const focusPrevious = useCallback(() => {
    const items = getSortedItems();
    if (items.length === 0) return;

    const currentId = activeIdRef.current;
    const currentIndex = items.findIndex(([id]) => id === currentId);
    const prevIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    const prevId = items[prevIndex][0];

    setActiveId(prevId);
    focusItem(prevId);
  }, [getSortedItems, focusItem]);

  /**
   * Move focus to the first item
   */
  const focusFirst = useCallback(() => {
    const items = getSortedItems();
    if (items.length === 0) return;

    const firstId = items[0][0];
    setActiveId(firstId);
    focusItem(firstId);
  }, [getSortedItems, focusItem]);

  /**
   * Move focus to the last item
   */
  const focusLast = useCallback(() => {
    const items = getSortedItems();
    if (items.length === 0) return;

    const lastId = items[items.length - 1][0];
    setActiveId(lastId);
    focusItem(lastId);
  }, [getSortedItems, focusItem]);

  /**
   * Handle keyboard events for navigation
   */
  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusNext();
          break;
        case 'ArrowUp':
          event.preventDefault();
          focusPrevious();
          break;
        case 'Home':
          event.preventDefault();
          focusFirst();
          break;
        case 'End':
          event.preventDefault();
          focusLast();
          break;
        default:
          break;
      }
    },
    [focusNext, focusPrevious, focusFirst, focusLast],
  );

  /**
   * Check if an item is active
   */
  const isItemActive = useCallback((id) => id === activeIdRef.current, []);

  /**
   * Get tabIndex for an item
   */
  const getTabIndex = useCallback(
    (id) => (id === activeId ? 0 : -1),
    [activeId],
  );

  const getItemPosition = useCallback(
    (itemId) => {
      const items = getSortedItems();
      return items.findIndex(([id]) => itemId === id);
    },
    [getSortedItems],
  );

  // Memoize the context value to prevent unnecessary re-renders
  // Note: activeId is included so consumers re-render when it changes
  const contextValue = useMemo(
    () => ({
      activeId,
      registerItem,
      unregisterItem,
      reset,
      handleKeyDown,
      isItemActive,
      getTabIndex,
      focusFirst,
      focusLast,
      getItemPosition,
    }),
    [
      activeId,
      registerItem,
      unregisterItem,
      reset,
      handleKeyDown,
      isItemActive,
      getTabIndex,
      focusFirst,
      focusLast,
      getItemPosition,
    ],
  );

  return contextValue;
};
